import { i18n, type SupportedLocale } from "./i18n";

const SESSION_KEY = "repo.i18n.lang";

function isSupported(value: string | null): value is SupportedLocale {
  return value === "fr" || value === "en";
}

function splitOnce(value: string, separator: string): [string, string | undefined] {
  const index = value.indexOf(separator);
  return index === -1
    ? [value, undefined]
    : [value.slice(0, index), value.slice(index + separator.length)];
}

/**
 * Persist the diner's explicit choice for THIS widget session (`sessionStorage`, cookie-free — the
 * widget runs in a third-party iframe where cookies are unreliable, ITP/third-party blocking). A
 * blocked storage (sandboxed iframe / SSR) is swallowed: a lost preference is non-fatal.
 */
export function rememberLanguage(lang: SupportedLocale): void {
  try {
    sessionStorage.setItem(SESSION_KEY, lang);
  } catch {
    // sessionStorage can throw in sandboxed iframes or during SSR — ignore.
  }
}

/** Recall a remembered choice for this session, or `undefined` (unset or corrupted value). */
export function recallLanguage(): SupportedLocale | undefined {
  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    return isSupported(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Carry an EXPLICIT language choice across the group-overflow redirect as a `?lang=` query param, so
 * it re-applies against the sister venue's (possibly different) default instead of resetting
 * (localization-i18n R7 cross-venue edge). Origin-agnostic: preserves other params + the hash and
 * works on absolute OR relative URLs without needing a base.
 */
export function withLangParam(url: string, lang: SupportedLocale): string {
  const [beforeHash, hash] = splitOnce(url, "#");
  const [path, query] = splitOnce(beforeHash, "?");
  const params = new URLSearchParams(query);
  params.set("lang", lang);
  const queryString = params.toString();
  return `${path}${queryString ? `?${queryString}` : ""}${hash ? `#${hash}` : ""}`;
}

/**
 * Apply a language IN PLACE (localization-i18n R7): switch i18next, stamp `<html lang>`, and remember
 * the choice — with NO navigation and NO URL change. Wire a `LanguageSwitcher`'s `onValueChange` to
 * this.
 */
export async function applyLanguage(lang: SupportedLocale): Promise<void> {
  await i18n.changeLanguage(lang);
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
  }
  rememberLanguage(lang);
}
